// Generic data-fetching hook with a browser cache: returns cached data
// instantly when fresh (no network), otherwise fetches and stores it.
// Re-fetches when `params` change or a global refresh is triggered.
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { dataCache, cacheKey, isServerBypass } from '../lib/dataCache';
import { useDataRefresh } from '../context/DataRefreshContext';

export function useApiData(path, params) {
  const { version } = useDataRefresh();
  const key = cacheKey(path, params);

  const [data, setData] = useState(() => dataCache.get(key) ?? null);
  const [loading, setLoading] = useState(() => dataCache.get(key) === undefined);
  const [error, setError] = useState(null);

  const fetchData = useCallback((force = false) => {
    const cached = dataCache.get(key);
    if (!force && cached !== undefined) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    // After a manual refresh, also bypass the server cache for truly-fresh data.
    const reqParams = isServerBypass() ? { ...(params || {}), refresh: 1 } : params;
    api
      .get(path, reqParams)
      .then((d) => { dataCache.set(key, d); setData(d); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [path, key, version]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { data, loading, error, refetch: () => fetchData(true) };
}
